/**
 * Advice terminus pill-row group ids + a shared live-clear helper
 * (advice-unified-terminus Phase C, §3.9). Under the single-pill contract at
 * most one of these is ever live, but a stale row can survive a lost durable
 * retirement — so the takeaway / practice-proposal / memo-proposal card
 * renderers and the input-group mount path all clear the WHOLE set, live and on
 * replay. Mirrors the backend `ADVICE_TERMINUS_PILL_GROUP_IDS`
 * (adviceIntentState.js) — keep the two lists in sync.
 *
 * @module core/adviceTerminusPills
 */

/**
 * Every Advice terminus pill-row group id (wrap-up + artifact + voice
 * correction, plus the retired legacy `key-takeaways` for hydration cleanup).
 *
 * `advice-wrap-up` is the standard-Advice response-4+ pill ("Sum this up"). It is
 * swept with the rest — the Key Takeaways card it produces must not leave it
 * standing beside itself — but its server-side eligibility is deliberately NOT
 * gated on the retirement marker. `build-a-practice` is no longer injected; it
 * stays listed because old transcripts can still carry it. `partner-memo-offer`
 * IS injected — by the `memo_compose` schedule from response 3 (label
 * "I'm ready, write the note").
 * @type {string[]}
 */
export const ADVICE_TERMINUS_PILL_GROUP_IDS = [
  'key-takeaways',
  'advice-wrap-up',
  'build-a-practice',
  'partner-memo-offer',
  'partner-memo-reply-offer',
  'voice-correction-explore',
  'voice-correction-act'
];

/**
 * Attribute stamped on a rendered Key Takeaways card (`<advice-takeaway>`), live
 * and on replay. The transcript is the only client-side record that a session's
 * takeaway has landed — there is no session-scoped flag — so this is what the
 * `request_takeaways` fire-guard reads.
 * @type {string}
 */
export const ADVICE_TAKEAWAY_CARD_ATTR = 'data-advice-takeaway';

/**
 * Whether a Key Takeaways card is present in the LIVE transcript. Scoped to
 * `#messages-list` on purpose: a past session replayed into the right panel
 * carries its own takeaway card and must not answer for the live conversation.
 *
 * @returns {boolean} True when this conversation has already landed a takeaway
 */
export function hasRenderedAdviceTakeaway() {
  return !!document
    .getElementById('messages-list')
    ?.querySelector(`[${ADVICE_TAKEAWAY_CARD_ATTR}]`);
}

/**
 * Remove every live Advice terminus pill from the floating PillRow. Idempotent
 * and cheap: a no-op when none are present (so replaying history cards on reload
 * never disturbs a legitimately-standing row). Never calls the consume API —
 * durable retirement is server-authoritative at each action's taken-signal site.
 *
 * @returns {void}
 */
export function clearAllAdviceTerminusPills() {
  try {
    const pillRow = window.app?.modules?.pillRow;
    const pills = pillRow?.agentPills || [];
    if (pills.length === 0) return;
    for (const pill of pills) {
      if (ADVICE_TERMINUS_PILL_GROUP_IDS.includes(pill.groupId)) {
        pillRow.removePill?.(pill.id, { animate: true });
      }
    }
  } catch (err) {
    console.warn('[adviceTerminusPills] terminus pill clear failed (non-blocking):', err);
  }
}
